import { useEffect, useState } from 'react'

import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark'

import { toastEventDispatch } from '@space-lib-events/toast'
import { ToastPosition } from '@space-lib-events/toast/enum'
import Button from '@space-lib/components/button'
import Icon from '@space-lib/components/icon'
import Modal from '@space-lib/components/modal'

import ModalBody from '@space-lib/components/modalBody'
import ModalFooter from '@space-lib/components/modalFooter'
import ModalHeader from '@space-lib/components/modalHeader'

import { Actions, Entities } from '@space-lib/datahub'
import { useDataHubEvents } from '@space-shell/common/hooks/useDataHubEvents'
import { useForm } from '@space-shell/common/hooks/useForm/useForm'

import useMutation from '@space-shell/common/hooks/useMutation'

import type { ExternalLinkPayload } from '@space-shell/common/services/externalLink'
import { ExternalLinkEndpoints } from '@space-shell/common/services/externalLink'
import { ExternalLinkService } from '@space-shell/common/services/externalLink'
import { queryClient } from '@space-shell/config/request'

import { useMembershipStore } from '@space-shell/core/store'
import { useTranslation } from 'react-i18next'

import { ExternalLinkModalType } from './enums'
import type { ExternalLinkFormFields, ExternalLinkModalContentProps } from './types'

import { ExternalLinkForm } from '../externalLinkForm'
import { validateForm } from '../utils'

const ExternalLinkModalContent = ({
  isOpen,
  handleClose,
  type,
  selectedLink
}: ExternalLinkModalContentProps) => {
  const { t } = useTranslation()
  const { membershipSlug } = useMembershipStore()
  const { data: formData, resetForm } = useForm<ExternalLinkFormFields>()
  const [isValidFormData, setIsValidFormData] = useState<boolean>(false)
  const { sendDataHubEvent } = useDataHubEvents()

  useEffect(() => {
    const validate = validateForm<ExternalLinkFormFields>(formData)
    setIsValidFormData(validate)
  }, [formData])

  const onClose = () => {
    resetForm()
    handleClose()
  }

  const callbacks = {
    onSuccess: () => {
      sendDataHubEvent({
        group: Entities.EXTERNAL_LINKS,
        name: type === ExternalLinkModalType.ADD ? Actions.ADD_SUCCESS : Actions.EDIT_SUCCESS,
        params: {
          externalLink: {
            icon: formData.icon.value,
            name: formData.name.value,
            url: formData.url.value
          }
        }
      })
      queryClient.invalidateQueries([ExternalLinkEndpoints.list(membershipSlug)])
      onClose()
    },
    onError: () => {
      toastEventDispatch({
        type: 'danger',
        message: t('external_links.messages.general_error'),
        position: ToastPosition.BOTTOM_RIGHT
      })
    }
  }

  const { mutate: addLinkMutate, isLoading: isLoadingAddLinkMutate } = useMutation(
    (payload: ExternalLinkPayload) => ExternalLinkService.add(membershipSlug, payload),
    { ...callbacks }
  )

  const { mutate: editLinkMutate, isLoading: isLoadingEditLinkMutate } = useMutation(
    (payload: ExternalLinkPayload) =>
      ExternalLinkService.update(selectedLink?.id as string, membershipSlug, payload),
    { ...callbacks }
  )

  const handleSave = () => {
    if (!isValidFormData) return

    const save = type === ExternalLinkModalType.ADD ? addLinkMutate : editLinkMutate

    save({
      icon: formData.icon.value,
      name: formData.name.value,
      url: formData.url.value
    })
  }

  const labels = {
    add: {
      title: t('external_links.add'),
      saveButton: t('external_links.add')
    },
    edit: {
      title: t('external_links.edit'),
      saveButton: t('external_links.save')
    }
  }

  return (
    <Modal show={isOpen} onClose={onClose}>
      <ModalHeader>
        <div className='_d-flex _align-items-baseline'>
          <h4 className='_text-4'>{labels[type].title}</h4>

          <button
            className='_p-0 _d-flex _border-0 _bg-transparent _text-gray-400 _ml-auto _cursor-pointer'
            onClick={onClose}>
            <span className='_sr-only'>close</span>
            <Icon icon={faXmark} className='_w-5 _h-5' />
          </button>
        </div>

        {type === ExternalLinkModalType.ADD && <p>{t('external_links.form.description')}</p>}
      </ModalHeader>
      <ModalBody>
        <ExternalLinkForm />
      </ModalBody>
      <ModalFooter>
        <div className='_w-full _d-flex _justify-content-end'>
          <Button variation='tertiary' className='_mr-4' onClick={onClose}>
            {t('general.close')}
          </Button>
          <Button
            variation='primary'
            onClick={handleSave}
            disabled={!isValidFormData}
            loading={isLoadingAddLinkMutate || isLoadingEditLinkMutate}>
            {labels[type].saveButton}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  )
}

export default ExternalLinkModalContent
